import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight, Mail, Shield, Check } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const GOLD = '#C9A961';

const AcademyForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/academy/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Endpoint always returns success — only network errors land here
      toast.error('Unable to reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12" data-testid="academy-forgot-page">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-10">
          <Link to="/academy" className="inline-block mb-6">
            <img
              src="/images/scholar-athlete-logo-new.png"
              alt="Scholar Athlete Academy"
              className="mx-auto h-20 w-auto"
              style={{ filter: `drop-shadow(0 0 40px rgba(255,94,0,0.25))` }}
            />
          </Link>
          <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
            <Shield className="w-3 h-3" style={{ color: GOLD }} />
            <span className="text-[9px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
              Password Reset
            </span>
          </div>
        </div>

        {sent ? (
          <div className="border-2 border-orange-500/60 p-8 text-center" data-testid="academy-forgot-success">
            <div className="w-12 h-12 mx-auto bg-orange-500 text-black flex items-center justify-center mb-5">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-black uppercase tracking-tight mb-2">Check Your Email</h2>
            <p className="text-sm text-gray-400 leading-relaxed">
              If an account exists for <strong className="text-white">{email}</strong>, we just sent a
              reset link. It expires in 1 hour.
            </p>
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-600 mt-6">
              Didn't get it? Check spam, then try again.
            </p>
            <Link
              to="/academy/login"
              className="mt-8 inline-block text-[11px] uppercase tracking-[0.25em] text-orange-500 hover:text-orange-400 font-bold"
            >
              ← Back to Sign In
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black uppercase tracking-tight text-center mb-2">
              Reset Your Password
            </h1>
            <p className="text-xs text-gray-500 tracking-widest uppercase text-center mb-8">
              Enter your email — we'll send a link
            </p>

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="academy-forgot-form">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.25em] text-gray-500 mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black border-2 border-gray-800 pl-10 pr-4 py-4 text-white font-mono text-sm focus:border-orange-500 outline-none transition-colors"
                    placeholder="you@school.edu"
                    data-testid="academy-forgot-email"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-gray-800 disabled:text-gray-600 text-black py-5 font-black text-sm tracking-[0.2em] uppercase transition-colors flex items-center justify-center gap-2"
                data-testid="academy-forgot-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Reset Link <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <Link
                to="/academy/login"
                className="text-[11px] uppercase tracking-[0.25em] text-gray-500 hover:text-orange-500 font-bold"
              >
                ← Back to Sign In
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AcademyForgotPassword;
